import * as layout from '../markLayout.module.css';
import React from 'react';
import Tag from '@hero/ui-kit/components/Tag';
import P from '@hero/ui-kit/typography/P';
import type { ValidateResponse } from '../../types';
import { describeErrorCode } from './constants';
type Props = {
    data: ValidateResponse | undefined;
    isLoading: boolean;
    // True while `data` is the previous document's result and the current one is in flight.
    isStale?: boolean;
    error: unknown;
};
const ValidateBannerContent: React.FC<Props> = ({ data, isLoading, error }) => {
    if (isLoading) {
        return (
            <P
                noDefaultMargin
                size="small"
                styles={{
                    color: 'var(--mark-muted)'
                }}
            >
                Validating...
            </P>
        );
    }
    if (error) {
        return (
            <div
                style={{
                    backgroundColor: 'var(--mark-danger-surface)',
                    color: 'var(--mark-danger)'
                }}
                className={layout.panel}
            >
                <P noDefaultMargin size="small">
                    Validation request failed
                </P>
            </div>
        );
    }
    if (!data) return null;
    if (data.valid) {
        return (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.8rem'
                }}
            >
                <Tag type="success" size="small">
                    Template valid
                </Tag>
                <P
                    noDefaultMargin
                    size="small"
                    styles={{
                        color: 'var(--mark-muted)'
                    }}
                >
                    Template content is valid. Publish gate runs at Blast/Drip Validate.
                </P>
            </div>
        );
    }
    return (
        <div
            style={{
                backgroundColor: 'var(--mark-danger-surface)'
            }}
            className={layout.panel}
        >
            <P
                noDefaultMargin
                size="small"
                styles={{
                    color: 'var(--mark-danger)',
                    fontWeight: 600,
                    marginBottom: '0.4rem'
                }}
            >
                Template has {data.errors.length} {data.errors.length === 1 ? 'error' : 'errors'}
            </P>
            <ul
                style={{
                    margin: 0,
                    paddingLeft: '1.6rem'
                }}
            >
                {data.errors.map((code, i) => (
                    <li
                        key={i}
                        style={{
                            color: 'var(--mark-danger)'
                        }}
                    >
                        {describeErrorCode(code)}
                    </li>
                ))}
            </ul>
        </div>
    );
};
// Live region stays mounted so screen readers hear each validation result change.
const ValidateBanner: React.FC<Props> = ({ isStale, ...props }) => (
    <div aria-live="polite">
        {isStale && (
            <P
                noDefaultMargin
                size="small"
                styles={{
                    color: 'var(--mark-muted)',
                    marginBottom: '0.4rem'
                }}
            >
                Validating latest changes...
            </P>
        )}
        <div style={isStale ? { opacity: 0.5 } : undefined}>
            <ValidateBannerContent {...props} />
        </div>
    </div>
);
export default ValidateBanner;
